/** Bounds the entire request, including body reads, and links caller cancellation. */
export const AUTH_REQUEST_TIMEOUT_MS = 10_000

export class RequestTimeoutError extends Error {
  constructor() {
    super('The server did not respond in time. Please try again.')
    this.name = 'TimeoutError'
  }
}

export function throwIfAborted(signal?: AbortSignal): void {
  if (signal?.aborted) throw signal.reason ?? new DOMException('Request canceled', 'AbortError')
}

export async function withRequestDeadline<T>(
  operation: (signal: AbortSignal) => Promise<T>,
  options: { signal?: AbortSignal; timeoutMs?: number } = {},
): Promise<T> {
  throwIfAborted(options.signal)
  const controller = new AbortController()
  let rejectCancellation!: (reason: unknown) => void
  const canceled = new Promise<never>((_, reject) => { rejectCancellation = reject })
  const abort = (reason: unknown) => {
    controller.abort(reason)
    rejectCancellation(reason)
  }
  const onAbort = () => abort(options.signal?.reason ?? new DOMException('Request canceled', 'AbortError'))
  options.signal?.addEventListener('abort', onAbort, { once: true })
  const timer = setTimeout(() => abort(new RequestTimeoutError()), options.timeoutMs ?? AUTH_REQUEST_TIMEOUT_MS)
  try {
    // The race also bounds broken transports which ignore AbortSignal.
    return await Promise.race([operation(controller.signal), canceled])
  } finally {
    clearTimeout(timer)
    options.signal?.removeEventListener('abort', onAbort)
  }
}
